import React, { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useGetPropertyImagesByInternalService } from '~/hooks/apis/accommodation/useGetPropertyImagesByInternalService';

import ApiError from '~/types/ApiError';

import type { PropertyRoomTypeWithImages } from '../types';

import ImportableImageGrid from './ImportableImageGrid';
import ImportableRoomImages from './ImportableRoomImages';

interface Props {
  existingImageIds: Array<string>;
  propertyId: string | undefined;
  scopedRoom?: PropertyRoomTypeWithImages;
  open: boolean;
  onClose: () => void;
  onImport?: (photoUnits: Array<{ id_cloudinary_external: string; title?: string }>) => Promise<void | number>;
}

export default function ImportImagesDialog({
  existingImageIds,
  propertyId,
  scopedRoom,
  open,
  onClose,
  onImport,
}: Props) {
  const [selectedImageIds, setSelectedImageIds] = useState<Array<string>>([]);
  const [isImporting, setIsImporting] = useState(false);
  const existingImageIdSet = useMemo(() => new Set(existingImageIds), [existingImageIds]);

  // propertyId doubles as internalServiceId; svc-accom 404s if unmapped (see isPropertyNotMapped below).
  const {
    data: propertyData,
    isLoading: isLoadingPropertyImages,
    isError: isPropertyImagesError,
    error: propertyImagesError,
  } = useGetPropertyImagesByInternalService({
    internalService: 'reservation',
    internalServiceId: propertyId,
    enabled: open && !scopedRoom,
  });
  const isPropertyNotMapped = propertyImagesError instanceof ApiError && propertyImagesError.status === 404;
  const propertyImages = propertyData?.images ?? [];
  const [roomImages, setRoomImages] = useState<Array<{ imageId: string; title: string | null }>>([]);
  const selectableImages = scopedRoom ? roomImages : propertyImages;

  function toggleSelected(imageId: string) {
    setSelectedImageIds((current) =>
      current.includes(imageId) ? current.filter((id) => id !== imageId) : current.concat(imageId),
    );
  }

  function handleClose() {
    setSelectedImageIds([]);
    onClose();
  }

  async function handleImport() {
    if (!onImport) {
      handleClose();
      return;
    }

    const photoUnits = selectableImages
      .filter((image) => selectedImageIds.includes(image.imageId))
      .map((image) => ({ id_cloudinary_external: image.imageId, title: image.title ?? undefined }));

    setIsImporting(true);
    try {
      await onImport(photoUnits);
      handleClose();
    } finally {
      setIsImporting(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import accommodation images</DialogTitle>
      <DialogContent dividers>
        {scopedRoom ? (
          <ImportableRoomImages
            roomId={scopedRoom.id}
            roomName={scopedRoom.name || 'this room'}
            open={open}
            existingImageIdSet={existingImageIdSet}
            selectedImageIds={selectedImageIds}
            onToggleSelected={toggleSelected}
            onImagesLoaded={setRoomImages}
          />
        ) : (
          <Stack spacing={1}>
            {isLoadingPropertyImages && (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            )}
            {!isLoadingPropertyImages && isPropertyImagesError && (
              <Alert severity="error">
                {isPropertyNotMapped
                  ? 'This property has no approved mapping in accommodation.'
                  : `Failed to load property images: ${
                      propertyImagesError instanceof Error ? propertyImagesError.message : 'Unknown error'
                    }`}
              </Alert>
            )}
            {!isLoadingPropertyImages && !isPropertyImagesError && propertyImages.length === 0 && (
              <Typography color="text.secondary">No images found for this property.</Typography>
            )}
            {!isLoadingPropertyImages && !isPropertyImagesError && propertyImages.length > 0 && (
              <ImportableImageGrid
                images={propertyImages}
                existingImageIdSet={existingImageIdSet}
                selectedImageIds={selectedImageIds}
                onToggleSelected={toggleSelected}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isImporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={selectedImageIds.length === 0 || isImporting || !onImport}
          onClick={handleImport}
          startIcon={isImporting ? <CircularProgress size={16} /> : undefined}
        >
          Import {selectedImageIds.length > 0 ? `(${selectedImageIds.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
